/**
 * Reduction d'image AVANT l'envoi — contrat de compression, doc 03.
 *
 * Le serveur refuse au-dela de 2 Mio, et une photo d'appareil moderne pese
 * 3 a 5 Mo : sans cette etape, l'utilisateur se heurterait a un refus qu'il ne
 * saurait pas contourner. La compression se fait dans le navigateur, donc ni
 * le reseau ni le serveur ne transportent l'original.
 */

import { appeler } from '../../api/client.ts';

/** Cote long vise, et qualite JPEG. */
export const COTE_MAX = 1600;
const QUALITE = 0.78;

export async function reduire(fichier: File): Promise<Blob> {
  const image = await createImageBitmap(fichier);

  const facteur = Math.min(1, COTE_MAX / Math.max(image.width, image.height));
  const largeur = Math.round(image.width * facteur);
  const hauteur = Math.round(image.height * facteur);

  const toile = document.createElement('canvas');
  toile.width = largeur;
  toile.height = hauteur;

  const contexte = toile.getContext('2d');
  if (contexte === null) {
    // Navigateur sans canvas : on envoie l'original, le serveur tranchera.
    return fichier;
  }
  contexte.drawImage(image, 0, 0, largeur, hauteur);
  image.close();

  const blob = await new Promise<Blob | null>((resoudre) => {
    toile.toBlob(resoudre, 'image/jpeg', QUALITE);
  });

  return blob ?? fichier;
}

/** Envoie une photo deja reduite. Le navigateur ecrit lui-meme la frontiere. */
/**
 * Envoie une photo réduite.
 *
 * Passe par le client d'API, et non par un `fetch` vers `/api/...` écrit en
 * dur : celui-ci ignorait `VITE_URL_API` et partait vers le serveur du
 * back-office dès que l'API vit sur un autre domaine. La réponse n'étant pas
 * celle de l'API, l'écran n'affichait qu'un « Envoi de la photo refusé » sans
 * explication.
 */
export async function envoyerPhoto<T>(produitId: string, fichier: File): Promise<T> {
  const reduite = await reduire(fichier);
  const formulaire = new FormData();
  formulaire.append('photo', reduite, 'photo.jpg');

  return appeler<T>(`/produits/${produitId}/photos`, {
    methode: 'POST',
    formulaire,
  });
}
